import subYears from 'date-fns/subYears'
import isBefore from 'date-fns/isBefore'
import Upload from 'components/uploader/upload';
import { orderStatus } from './orderStatus';
import { priorityList, prioritySettings } from './priorityList';
import { escapedRegex } from './escapedRegex';
import { filterDropdown } from './filterDropdown';
import { getNoteText } from './getNoteText';
import { parseISO } from "date-fns";
import { format, utcToZonedTime } from "date-fns-tz";
import { mutableColumns } from './mutableColumns';
import { displayActiveCards, getCardsCount, activeCardTypes } from './displayActiveCards';
import { toHoursAndMinutes, toHoursAndMinutesString } from './toHoursAndMinutes';
import { cardValidation } from './cardValidator';
import { fullDateDiff } from './fullDateDiff';
import { toCamelCase } from './toCamelCase';
import { getEventData } from './getEventData';
import { inspectionIcons } from './inspectionIcons';
import { defaultIncidentTypes } from './defaultIncidentTypes';
import { translateText } from './translateText';
import { getOccurrenceSchedule, getOccurrence, recurringDays } from './getOccurrenceSchedule';
import moment from 'moment';
import testFilter from './testFilter';
import { getFileNameFromUrl } from './decodeUrl';
import { dirtyValuesFor } from './dirtyValues';
import { getSuggestedCategories } from './getSuggestedCategories';
import { getTimeAgoShortFormat } from './getTimeAgoShortFormat';
import React from 'react';
import preferencesManager from './updatePropertySelection';
import { getSelectedTimeSpanName, getSelectedTimeSpanByDate, getSelectedTimeSpanByDateSubscription, getDatesFromTimeSpan, getSelectedTimeByDate } from './getSelectedTimeSpan';
import { getDropdownIcons, getIconByName, getObjectIcons, getPriorityIconByName, getPriorityIcons } from './dropdownIcons';
import { tagColors } from './tagColors';
import getIsMobile from './getIsMobile';
import { formatPrice } from './formatPrice';
import { getAreasWithCost, getTotalCost } from './inspectionCost';
import { colorShade } from './colorShade';
import { getOrdinalSuffix } from './getOrdinalSuffix';
import cron from './cron';
import { isUSPhoneNumber, isCanadaPhoneNumber } from './testPhoneNumber';
import { getWeekDayIndex, weekdays, getWeekDayIdByIndex } from './weekdays';
import { months } from './months';
import { getSubscriptionIcons } from './subscriptionIcon';
import { getReportType, getDisplayFilters, populateModalFromSubscription, getDisplayRowFilters } from './populateModalFromSubscription';
import { formatDateInput } from './dateTransform';
import { getFilteredMakeReadyCategories } from './getFilteredMakeReadyCategories';
import { getRandomValue } from './getRandomValue';
import { sanitizeInputValue } from './sanitize';
import { getItemsProgress } from './itemsProgress';
import { isItemComplete } from './isItemComplete';
import { getVideoMetaData } from './getVideoMetaData';
import { generateVideoThumbnail } from './generateVideoThumbnail';
import exportToPDF from './csvToPDF';

const isAdult = (date) => {
  const dob = moment(date);
  const start = moment().subtract(window.MINIMUM_AGE, 'years');
  return dob.isBefore(start);
}

const capitalize = (string) => {
  if (!string) return '';
  return `${string[0].toUpperCase()}${string.substr(1, string.length)}`
};

const capsLock = (string) => {
  if (!string) return '';
  return string.toUpperCase();
}

const titleize = (string) => {
  return string.replace(/_/g, ' ').split(' ').map(s => capitalize(s)).join(' ');
};

const toCurr = (num, symbol = '$') => {
  if (!num && num !== 0) return '';
  return symbol + parseFloat(num).toFixed(2).replace(/\B(?=(\d{3})+(?!\d))/g, ",");
};

const toAccounting = (num) => {
  if (!num && num !== 0) return '-';
  const parsed = parseFloat(num).toFixed(2).replace(/\B(?=(\d{3})+(?!\d))/g, ",");
  if (num < 0) {
    return `(${parsed.substr(1)})`
  } else {
    return parsed;
  }
};

const percentageCalculator = (high, low) => {
  if (high === 0 || low === 0) return 0.00;
  return (low / high) * 100
};

const toPercent = (num) => {
  if (num === "N/A") return "N/A";
  return num.toFixed(2)
};


const timeFrames = [
  { displayName: 'Today', id: 'today' },
  { displayName: 'Last 7 days', id: 'last 7' },
  { displayName: 'Last 30 days', id: 'last 30' },
  { displayName: 'Last 12 months', id: 'last year' },
  { displayName: 'All time', id: 'all time' },
  { displayName: 'Month to date', id: 'mtd' },
  { displayName: 'Year to date', id: 'ytd' },
];

const prepad = (number, places = 6) => {
  const initial = `00000${number}`;
  return initial.substring(initial.length - places);
};

const boldMatchCharacters = ({ text = "", match = "" }) => {
  const regEx = new RegExp(match, 'gi');
  return <div dangerouslySetInnerHTML={{ __html: text.replace(regEx, '<strong>$&</strong>') }}></div>
}

const italicCharacters = ({ text = "" }) => {
  return <div dangerouslySetInnerHTML={{ __html: text }}></div>
}

const LangConverter = {
  ones: ['', 'one', 'two', 'three', 'four', 'five', 'six', 'seven', 'eight', 'nine'],
  tens: ['', '', 'twenty', 'thirty', 'forty', 'fifty', 'sixty', 'seventy', 'eighty', 'ninety'],
  teens: [...Array(10)].concat(['ten', 'eleven', 'twelve', 'thirteen', 'fourteen', 'fifteen', 'sixteen', 'seventeen', 'eighteen', 'nineteen']),
  toLangConvert(num) {
    const hundreds = Math.floor(num / 100);
    const remainder = num - (hundreds * 100);
    const numTens = Math.floor(remainder / 10);
    const numOnes = remainder - (numTens * 10);
    const numTeens = (numTens * 10) + numOnes;
    const { ones, tens, teens, append } = LangConverter;
    return `${append(ones[hundreds], 'hundred')} ${tens[numTens] || ''} ${teens[numTeens] || ones[numOnes] || ''}`.replace(/\s+/g, ' ');
  },
  append(num, unit) {
    return (num && num !== ' ') ? `${num} ${unit}` : '';
  },
  convertCents(number) {
    const cents = number % 1;
    if (cents === 0) return '';
    const numCents = cents.toFixed(2).replace('0.', '');
    return ` and ${numCents}/100`;
  },
  numToLang(number) {
    const { toLangConvert, append, convertCents } = LangConverter;
    const thousands = Math.floor(number / 1000);
    const hundreds = Math.floor(number - (thousands * 1000));
    return `${append(toLangConvert(thousands), 'thousand')} ${toLangConvert(hundreds)}${convertCents(number)}`.replace(/\s+/g, ' ').replace(/^\s/g, '');
  }
};

const { numToLang } = LangConverter;

const safeRegExp = (string) => {
  try {
    return new RegExp(string, 'i')
  } catch (e) {
    return new RegExp(string.replace(/[\-\[\]\/\{\}\(\)\*\+\?\.\\\^\$\|]/g, "\\$&"), 'i');
  }
};

const emailRegExp = (email) => {
  return /^\w+([\.-]?\w+)*@\w+([\.-]?\w+)*(\.\w{2,3})+$/.test(email)
}

//TO BE USED TO SEE IF THE PASSED IN USER ID IS THE SAME AS THE CURRENT USER
//MUST BE PASSED IN AS A STRING
const isUser = (userID) => {
  return parseInt(window?.user?.id) === userID || userID === window?.user?.id;
};

const currentUserId = () => {
  return parseInt(window.user.id) || userID === window.user.id;
}

const b64toBlob = (b64Data, contentType = '', sliceSize = 512) => {
  const byteCharacters = atob(b64Data);

  const byteArrays = [];

  for (let offset = 0; offset < byteCharacters.length; offset += sliceSize) {
    const slice = byteCharacters.slice(offset, offset + sliceSize),
      byteNumbers = new Array(slice.length);
    for (let i = 0; i < slice.length; i++) {
      byteNumbers[i] = slice.charCodeAt(i);
    }
    const byteArray = new Uint8Array(byteNumbers);

    byteArrays.push(byteArray);
  }

  return new Blob(byteArrays, { type: contentType });
};

const sum = (array, field) => array.reduce((total, item) => (field ? item[field] : item) + total, 0);

const toQueryString = (params) => Object.keys(params).reduce((s, k) => s + `${k}=${params[k]}&`, '?').slice(0, -1);

const isOver18Years = (dob) => {
  const start = subYears(new Date(), window?.MINIMUM_AGE || 18)
  const isOver18 = isBefore(new Date(dob), start)

  return isOver18
}

const getDebounce = () => {
  let timer;
  return (fn, d) => {
    if (timer) clearTimeout(timer);
    timer = setTimeout(fn, d);
  }
}

const base64ToBlob = async (rawBase64) => {
  const base64 = await fetch(rawBase64);
  const blob = await base64.blob();
  return blob
}

const changeUpload = async (file, setter, state, key) => {
  if (!file) return
  const upload = new Upload(file)
  const reader = new FileReader();
  reader.readAsDataURL(file);
  reader.onload = () => {
    setter({ ...state, [key]: reader.result })
  };

  await upload.upload()
  return { uuid: upload.uuid || null, url: reader.result || '' }
}
const isObject = (obj) => {
  return typeof obj === 'object' && !Array.isArray(obj) && obj !== null
}

const executeOnce = (fn) => {
  let executed = false;
  return (...args) => {
    if (!executed) {
      executed = true
      return fn(...args)
    }
  };
}


const formatInTimeZone = (date, fmt, tz) =>
  format(utcToZonedTime(parseISO(date), tz), fmt, { timeZone: tz });

const replaceDoubleSpaceToSingle = (value) => {
  return value.replace(/\s+/g, ' ');
}

const trimText = (value) => value && typeof value === 'string' ? value.trim() : value;

const maskPhoneNum = (value) => {
  const x = value.replace(/\D/g, '').match(/(\d{0,3})(\d{0,3})(\d{0,4})/);
  const maskedValue = !x[2] ? x[1] : '(' + x[1] + ') ' + x[2] + (x[3] ? '-' + x[3] : '');

  return maskedValue;
}

// expected argument: null | undefined | true | false 
const getServicesCalledStatus = (emergency_services_called) => {
  if (emergency_services_called == null) return '';
  return emergency_services_called === true ? 'Yes' : 'No'
}

const getOccupiedUnitStatus = (emergency_services_called) => {
  if (emergency_services_called == null) return '';
  return emergency_services_called === 'Yes' ? 'Yes' : 'No'
}

const getLookUpMapFromArray = (list) => {
  const map = {}
  list.forEach(item => map[item.name] = item)
  return map
}

const getCurrentDateTime = () => {
  // gives date time in this format -> "November 9, 2023 3:42 PM"
  const d = new Date()
  let date = new Intl.DateTimeFormat('en-us', {
    year: "numeric", month: "long", day: "numeric",
  }).format(d)

  let time = new Intl.DateTimeFormat('en-us', {
    timeStyle: 'short',
  }).format(d)

  return `${date} ${time}`
}

const userHasPermission = () => {
  if (!window.permissions) return false;

  let isAllowed = false;
  const permissionKeys = ["property_settings", "system_settings"]
  Object.keys(window.permissions).forEach(key => {
    permissionKeys.includes(key) ? isAllowed = true : isAllowed = false
  })
  return isAllowed
}

const waitForMiliSeconds = (ms) => new Promise((resolve) => setTimeout(resolve, ms))
const isNullOrUndefined = (value) => value === null || value === undefined;

const isJson = (string) => {
  try {
    return JSON.parse(string);
  } catch (e) {
    return string
  }
}

const OPEN_FROM = {
  TABLE: "TABLE",
  MODAL: "MODAL",
}


export {
  capitalize,
  titleize,
  toCurr,
  toAccounting,
  toPercent,
  numToLang,
  prepad,
  safeRegExp,
  emailRegExp,
  sum,
  isUser,
  b64toBlob,
  toQueryString,
  percentageCalculator,
  capsLock,
  currentUserId,
  getDebounce,
  isOver18Years,
  base64ToBlob,
  changeUpload,
  isObject,
  executeOnce,
  orderStatus,
  priorityList,
  prioritySettings,
  escapedRegex,
  filterDropdown,
  getNoteText,
  formatInTimeZone,
  displayActiveCards,
  getCardsCount,
  activeCardTypes,
  mutableColumns,
  replaceDoubleSpaceToSingle,
  trimText,
  toHoursAndMinutes,
  cardValidation,
  fullDateDiff,
  toCamelCase,
  getEventData,
  toHoursAndMinutesString,
  inspectionIcons,
  defaultIncidentTypes,
  translateText,
  getOccurrenceSchedule,
  getOccurrence,
  maskPhoneNum,
  recurringDays,
  isAdult,
  testFilter,
  getFileNameFromUrl,
  getServicesCalledStatus,
  dirtyValuesFor,
  getLookUpMapFromArray,
  getDatesFromTimeSpan,
  getSuggestedCategories,
  getTimeAgoShortFormat,
  getCurrentDateTime,
  boldMatchCharacters,
  italicCharacters,
  userHasPermission,
  preferencesManager,
  getSelectedTimeSpanName,
  getSelectedTimeSpanByDate,
  getSelectedTimeByDate,
  getDropdownIcons,
  getIconByName,
  getObjectIcons,
  tagColors,
  waitForMiliSeconds,
  isJson,
  isNullOrUndefined,
  getIsMobile,
  formatPrice,
  getAreasWithCost,
  getTotalCost,
  colorShade,
  getOccupiedUnitStatus,
  getOrdinalSuffix,
  isUSPhoneNumber,
  isCanadaPhoneNumber,
  cron,
  getWeekDayIndex,
  getWeekDayIdByIndex,
  weekdays,
  getSelectedTimeSpanByDateSubscription,
  months,
  getSubscriptionIcons,
  getReportType,
  getDisplayFilters,
  populateModalFromSubscription,
  getDisplayRowFilters,
  timeFrames,
  formatDateInput,
  getFilteredMakeReadyCategories,
  getRandomValue,
  sanitizeInputValue,
  getVideoMetaData,
  getPriorityIconByName,
  getPriorityIcons,
  isItemComplete,
  exportToPDF,
  OPEN_FROM,
  getItemsProgress,
  generateVideoThumbnail
};
